import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  CircularProgress,
  useTheme,
  alpha,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { toast } from 'react-toastify';
import proxy from '../api/axios';

const ACCENT = '#137274';

const FeedbackForm = ({ open, handleClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [feedback, setFeedback] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.12)';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const surface = isDark ? '#161b22' : '#ffffff';

  useEffect(() => {
    if (!open) return;
    setFeedback('');
    // keep name if user already typed in a previous open this session
  }, [open]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Enter your name to share feedback');
      return;
    }
    if (!feedback.trim()) {
      toast.error('Enter your feedback');
      return;
    }

    setSubmitting(true);
    try {
      const url = window.location.href;
      await proxy.post('/feedback', {
        feedback: feedback.trim(),
        name: name.trim(),
        url,
      });
      toast.success('Thanks for your feedback.');
      setFeedback('');
      handleClose();
    } catch (error) {
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: surface,
          backgroundImage: 'none',
          overflow: 'hidden',
          mx: 2,
        },
      }}
    >
      <Box
        sx={{
          px: 2.75,
          pt: 2.25,
          pb: 1.75,
          background: isDark
            ? `linear-gradient(135deg, ${alpha(ACCENT, 0.22)}, transparent)`
            : `linear-gradient(135deg, ${alpha(ACCENT, 0.1)}, transparent)`,
          borderBottom: `1px solid ${border}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box>
            <Chip
              size="small"
              icon={<ChatBubbleOutlineRoundedIcon sx={{ fontSize: '14px !important' }} />}
              label="Feedback"
              sx={{
                mb: 1,
                height: 24,
                fontWeight: 800,
                bgcolor: alpha(ACCENT, isDark ? 0.25 : 0.12),
                color: ACCENT,
                '& .MuiChip-icon': { color: ACCENT },
              }}
            />
            <DialogTitle
              sx={{
                p: 0,
                fontWeight: 900,
                fontSize: '1.25rem',
                letterSpacing: '-0.03em',
              }}
            >
              Help us improve CrafterHack
            </DialogTitle>
            <Typography variant="body2" sx={{ color: muted, mt: 0.75, maxWidth: 420, lineHeight: 1.5 }}>
              Bugs, ideas, or what’s working well — a few lines go a long way.
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            disabled={submitting}
            size="small"
            aria-label="Close feedback"
            sx={{ color: muted }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <DialogContent sx={{ px: 2.75, pt: 2.5, pb: 1 }}>
        <TextField
          label="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          required
          autoFocus
          disabled={submitting}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: ACCENT,
            },
            '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
          }}
        />
        <TextField
          multiline
          minRows={5}
          label="Your feedback"
          placeholder="What should we fix, add, or keep doing?"
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          fullWidth
          required
          disabled={submitting}
          sx={{
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: ACCENT,
            },
            '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
          }}
        />
        <Typography variant="caption" sx={{ display: 'block', mt: 1.25, color: muted }}>
          We’ll include the page you’re on so we can debug faster.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 2.75, pb: 2.5, pt: 1.5, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={submitting}
          sx={{ textTransform: 'none', fontWeight: 700, color: muted }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />
          }
          sx={{
            textTransform: 'none',
            fontWeight: 800,
            px: 2.25,
            bgcolor: ACCENT,
            boxShadow: `0 8px 20px ${alpha(ACCENT, 0.28)}`,
            '&:hover': { bgcolor: '#0f5c5e' },
          }}
        >
          {submitting ? 'Sending…' : 'Send feedback'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackForm;
